const ensurePdfFilename = (name) => {
  const trimmed = String(name || "document").trim() || "document";
  return trimmed.toLowerCase().endsWith(".pdf") ? trimmed : `${trimmed}.pdf`;
};

export const exportFinalizedPdf = async (instance, filename) => {
  const documentViewer = instance?.Core?.documentViewer;
  const annotationManager = instance?.Core?.annotationManager;
  const pdfDocument = documentViewer?.getDocument?.();

  if (!pdfDocument || !annotationManager) {
    throw new Error("The PDF is still loading. Please try again in a moment.");
  }

  if (typeof pdfDocument.getDocumentCompletePromise === "function") {
    await pdfDocument.getDocumentCompletePromise();
  }

  const xfdfString = await annotationManager.exportAnnotations();
  const data = await pdfDocument.getFileData({
    xfdfString,
    flatten: true,
    doctype: "pdf",
  });

  return new File([data], ensurePdfFilename(filename), {
    type: "application/pdf",
  });
};

export const reloadFinalizedPdf = (
  instance,
  finalizedSource,
  {
    timeoutMs = 30000,
    filename = finalizedSource?.name || "document.pdf",
    streaming = true,
    withCredentials = false,
  } = {}
) =>
  new Promise((resolve, reject) => {
    const documentViewer = instance?.Core?.documentViewer;
    const ui = instance?.UI;
    if (!documentViewer || typeof ui?.loadDocument !== "function") {
      reject(new Error("The PDF viewer could not reload the finalized file."));
      return;
    }

    let settled = false;
    const loadErrorEvent = ui.Events?.LOAD_ERROR;
    const timeout = setTimeout(() => {
      finish(
        reject,
        new Error("The PDF viewer timed out while loading the finalized file.")
      );
    }, timeoutMs);

    const cleanup = () => {
      clearTimeout(timeout);
      documentViewer.removeEventListener?.("documentLoaded", handleLoaded);
      if (loadErrorEvent) {
        ui.removeEventListener?.(loadErrorEvent, handleLoadError);
      }
    };
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback(value);
    };
    const handleLoaded = () => finish(resolve);
    const handleLoadError = (error) => {
      ui.closeElements?.(["errorModal"]);
      finish(
        reject,
        error instanceof Error
          ? error
          : new Error("The PDF viewer could not reload the finalized file.")
      );
    };

    documentViewer.addEventListener?.("documentLoaded", handleLoaded);
    if (loadErrorEvent) {
      ui.addEventListener?.(loadErrorEvent, handleLoadError);
    }

    try {
      const loadResult = ui.loadDocument(finalizedSource, {
        filename,
        extension: "pdf",
        streaming,
        withCredentials,
      });
      loadResult?.catch?.(handleLoadError);
    } catch (error) {
      handleLoadError(error);
    }
  });

export const mergeFinalizedDocumentIntoTabs = (
  tabs,
  tabId,
  finalizedDocument
) =>
  tabs.map((tab) =>
    tab.id === tabId
      ? {
          ...tab,
          title: finalizedDocument?.name || tab.title,
          documentRecord: {
            ...(tab.documentRecord || {}),
            ...finalizedDocument,
          },
        }
      : tab
  );
